import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, HandshakeIcon, Compass, Wrench } from "lucide-react";
import { SectionHeading, Eyebrow } from "../components/SectionHeading";
import SEO from "../components/SEO";
import { useSiteAbout } from "../hooks/useSiteContent";
import { resolveAssetUrl } from "../lib/api";

const VALUES = [
  {
    icon: Award,
    title: "Quality First",
    body: "Every order inspected, every grade verified, every load secured.",
    bg: "bg-[#0A192F]",
    text: "text-white",
    iconBg: "bg-[#D4AF37]",
    iconColor: "text-[#0A192F]",
  },
  {
    icon: HandshakeIcon,
    title: "Built on Trust",
    body: "Long-term partnerships with builders, contractors, and developers.",
    bg: "bg-[#D4AF37]",
    text: "text-[#0A192F]",
    iconBg: "bg-[#0A192F]",
    iconColor: "text-[#D4AF37]",
  },
  {
    icon: Compass,
    title: "Always Local",
    body: "Regional sourcing where possible, fast turnaround where it matters.",
    bg: "bg-[#0A192F]",
    text: "text-white",
    iconBg: "bg-[#D4AF37]",
    iconColor: "text-[#0A192F]",
  },
  {
    icon: Wrench,
    title: "Field Tested",
    body: "Our team has walked job sites - we know what builders actually need.",
    bg: "bg-[#D4AF37]",
    text: "text-[#0A192F]",
    iconBg: "bg-[#0A192F]",
    iconColor: "text-[#D4AF37]",
  },
];

const About = () => {
  const about = useSiteAbout() || {};
  const story = about.story_body ||
    "CM Lindley was built on a simple idea: contractors deserve a supplier that thinks like a builder. That means tight grades, accurate counts, on-time delivery, and a phone that gets answered.";
  const paragraphs = story.split(/\n\s*\n/);

  return (
    <div data-testid="about-page">
      <SEO
        title="About - Builder-First Material Supply"
        path="/about"
        description="CM Lindley is a builder-first material supplier - mill-direct sourcing, accurate counts, on-time delivery, and the kind of hands-on service contractors expect."
      />

      <section className="bg-[#0A192F] py-20 sm:py-24 lg:py-32" data-testid="about-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <Eyebrow light>About CM Lindley</Eyebrow>
          <h1 className="mt-5 sm:mt-6 font-display font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            Material Supply, <span className="text-[#D4AF37]">Reimagined.</span>
          </h1>
          <p className="mt-6 sm:mt-8 text-white/75 max-w-2xl text-base sm:text-lg">
            CM Lindley is a builder-first material supplier - combining mill-direct sourcing with the
            kind of hands-on service contractors used to get from the local yard.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-24 lg:py-32 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden order-2 lg:order-1">
            <img src={resolveAssetUrl(about.image_url)} alt="Wood framing under construction" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="mt-4 sm:mt-5 font-display font-black tracking-tighter text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl">
              {about.story_title || "Founded to serve builders the right way."}
            </h2>
            <div className="mt-7 sm:mt-8 space-y-4 sm:space-y-5 text-[#0A192F]/75 leading-relaxed">
              {paragraphs.map((p, i) => <p key={`p-${i}-${p.slice(0, 16)}`}>{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 lg:py-32 bg-[#F0F4F8]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <SectionHeading eyebrow="Core Values" title="What we stand for." align="left" />
          <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {VALUES.map(({ icon: Icon, title, body, bg, text, iconBg, iconColor }, i) => (
              <div
                key={title}
                data-testid={`about-value-${i}`}
                className={`${bg} ${text} p-7 sm:p-8 hover-lift transition-transform`}
              >
                <div className={`w-14 h-14 ${iconBg} flex items-center justify-center`}>
                  <Icon className={iconColor} size={28} strokeWidth={2.25} />
                </div>
                <h3 className={`mt-6 sm:mt-7 font-display font-black text-xl sm:text-2xl tracking-tight ${text}`}>
                  {title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${text} opacity-90`}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 lg:py-32 bg-white" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-1">
            <Eyebrow>Why Choose Us</Eyebrow>
            <h2 className="mt-4 sm:mt-5 font-display font-black tracking-tighter text-[#0A192F] text-3xl sm:text-4xl">
              Built around the way you build.
            </h2>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6 sm:gap-8">
            {[
              ["Fast Turnaround", "We move quickly on every quote so your schedule doesn't have to wait on materials."],
              ["Spec-Accurate", "Lumber grades, steel mill certs, fastener specs - everything documented, everything verified."],
              ["Job-Site Ready", "Materials staged, bundled, and delivered the way your crew works."],
              ["Single Point of Contact", "One person owns your order from quote to delivery. No transferring, no excuses."],
            ].map(([title, body]) => (
              <div key={title} className="border-l-2 border-[#D4AF37] pl-5 sm:pl-6">
                <h3 className="font-display font-bold text-lg text-[#0A192F]">{title}</h3>
                <p className="mt-2 text-sm text-[#0A192F]/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A192F] py-20 sm:py-24" data-testid="about-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 text-center">
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Let's build something together.
          </h2>
          <Link
            to="/contact"
            data-testid="about-bottom-cta"
            className="mt-7 sm:mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#D4AF37] text-[#0A192F] px-8 sm:px-10 py-4 sm:py-5 font-display text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold hover:bg-white transition-colors"
          >
            Start a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
